import { bool, func, object } from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { createFragmentContainer, graphql } from 'react-relay';
import get from 'lodash.get';
import { isMasqueradingAsBuyer } from 'dibs-cookie-jar';
import { localStorage } from 'dibs-browser-storage';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';

import Dropdown, { alignConstants, sizeConstants } from '../Dropdown/Dropdown';
import DropdownBody from '../Dropdown/DropdownBody';
import authModalLoader from '../../utils/AuthModalLoader';
import AccountNavTrigger from './AccontNavTrigger';
import BuyerAccountNavContent from './AccountNavContent';

import { ACTION_REGISTER, ACTION_LOGIN } from './accountConstants';
import styles from './AccountNav-style.scss';
import { actionCreators } from '../../actions';

import DropdownLoadingContent from '../Dropdown/DropdownLoadingContent';

import asyncTradeAccountNavContent from './asyncTradeAccountNavContent';

class AccountNav extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            isMasquerading: false,
            TradeAccountNavContent: null,
        };
        this.handleClick = this.handleClick.bind(this);
        this.loadComponent = this.loadComponent.bind(this);
    }

    componentDidMount() {
        /* eslint-disable react/no-did-mount-set-state */
        this.setState({
            isMasquerading: isMasqueradingAsBuyer(document.cookie),
        });
        /* eslint-enable react/no-did-mount-set-state */
    }

    loadComponent() {
        if (get(this.props.user, 'isVerifiedTrade')) {
            asyncTradeAccountNavContent().then(TradeAccountNavContent => {
                this.setState({ AccountNavContent: TradeAccountNavContent });
            });
        } else {
            this.setState({ AccountNavContent: BuyerAccountNavContent }); // no need to import async
        }
    }

    handleClick(e) {
        e.preventDefault();
        const action = e.currentTarget.dataset.action;
        Promise.resolve(
            authModalLoader.show({
                relayEnvironment: this.props.relay.environment,
                email: localStorage.getItem('userEmail'),
                action,
                ga: {
                    label: `${action} link - global header`,
                    value: !!window.scrollY,
                },
            })
        )
            .then(({ err }) => {
                if (!err) {
                    this.props.dispatch(actionCreators.setUpdateUserState(true));
                }
            })
            .catch(err => {
                console.warn('Auth Modal could not be loaded\n', err);
            });
    }

    render() {
        const { hasUser, hasUserId, isClient, user, isVipCuratedFolderTooltipVisible } = this.props;
        const { AccountNavContent } = this.state;

        if (!isClient || (hasUserId && !hasUser)) {
            return null;
        }

        const name = get(user, 'profile.firstName') || (
            <FormattedMessage id="dbl.Header.AccountNav.name" defaultMessage={'My Account'} />
        );

        const totalUnreadConversations = get(user, 'conversationSummary.unread') || 0;
        const isVerifiedTrade = get(user, 'isVerifiedTrade');
        const tradeLoyaltyStatus = get(user, 'tradeFirm.loyalty.status') || '';

        return (
            <div data-tn="account-nav" className={styles.wrapper}>
                {hasUser ? (
                    <Dropdown
                        align={alignConstants.ALIGN_CENTER}
                        size={sizeConstants.SIZE_SMALL}
                        onShow={this.loadComponent}
                        className={classnames({
                            [styles.noHover]: isVipCuratedFolderTooltipVisible,
                        })}
                    >
                        <AccountNavTrigger
                            isMasquerading={this.state.isMasquerading}
                            isVerifiedTrade={isVerifiedTrade}
                            name={name}
                        />
                        <DropdownBody>
                            {AccountNavContent ? (
                                <AccountNavContent
                                    totalUnreadConversations={totalUnreadConversations}
                                    tradeLoyaltyStatus={tradeLoyaltyStatus}
                                    viewer={this.props.viewer}
                                    user={user}
                                />
                            ) : (
                                <DropdownLoadingContent />
                            )}
                        </DropdownBody>
                    </Dropdown>
                ) : (
                    <div className={styles.authLinks}>
                        <a
                            className={styles.authLink}
                            data-tn="user-menu-nav-item-link-log-in"
                            data-action={ACTION_LOGIN}
                            onClick={this.handleClick}
                        >
                            <FormattedMessage
                                id="dbl.Header.AccountNav.log_in"
                                defaultMessage={'Log In'}
                            />
                        </a>
                        <a
                            className={styles.authLink}
                            data-tn="user-menu-nav-item-link-sign-up"
                            data-action={ACTION_REGISTER}
                            onClick={this.handleClick}
                        >
                            <FormattedMessage
                                id="dbl.Header.AccountNav.sign_up"
                                defaultMessage={'Sign Up'}
                            />
                        </a>
                    </div>
                )}
            </div>
        );
    }
}

AccountNav.propTypes = {
    hasUser: bool,
    hasUserId: bool,
    isClient: bool,
    user: object,
    viewer: object,
    relay: object,
    dispatch: func,
    isVipCuratedFolderTooltipVisible: bool,
};

export default createFragmentContainer(
    connect(({ header }) => {
        const isVipCuratedFolderTooltipVisible = get(header, 'isVipCuratedFolderTooltipVisible');
        return {
            isVipCuratedFolderTooltipVisible,
        };
    })(AccountNav), // for dispatch
    {
        viewer: graphql`
            fragment AccountNav_viewer on Viewer {
                ...AccountNavContent_viewer
            }
        `,
        user: graphql`
            fragment AccountNav_user on User {
                ...AccountNavContent_user
                isVerifiedTrade
                profile {
                    firstName
                }
                conversationSummary {
                    unread
                }
                tradeFirm {
                    profileStatus
                    publicProfile {
                        urlLabel
                    }
                    loyalty {
                        status
                    }
                }
            }
        `,
    }
);
